import type { Item, ItemKind, ItemSlot } from "./characters";
import type { ImageReference, PanelInfo } from "./images";

export const itemKindLabels: Record<ItemKind, string> = {
  wearable: "Wearable",
  prop: "Prop",
  scene: "Scene",
};
export const itemKindOf = (item: Pick<Item, "kind">): ItemKind => item.kind ?? "wearable";
export const itemSlotOf = (item: Pick<Item, "slot">): ItemSlot =>
  item.slot === "head" || item.slot === "accessory" ? item.slot : "body";
export const isEquippableItem = (item: Pick<Item, "kind">) => itemKindOf(item) === "wearable";

/** Use frames, not character sprite bounds: props can have wide or tall panels. */
export function itemImagePanels(image?: ImageReference): PanelInfo[] {
  const config = image?.panels;
  const candidates = config
    ? Object.entries(config.details ?? {}).map(([name, detail]) => ({
        width: config.width,
        height: config.height,
        rows: config.rows,
        columns: config.columns,
        ...detail,
        name,
      }))
    : image?.selectedPanel
      ? [image.selectedPanel]
      : [];
  return candidates
    .filter((p) => {
      const f = p.frame;
      return (
        !p.disabled &&
        f &&
        [p.width, p.height, f.left, f.top, f.width, f.height].every(Number.isFinite) &&
        p.width > 0 &&
        p.height > 0 &&
        f.left >= 0 &&
        f.top >= 0 &&
        f.width > 0 &&
        f.height > 0 &&
        f.left + f.width <= p.width &&
        f.top + f.height <= p.height
      );
    })
    .sort((a, b) => a.row - b.row || a.column - b.column || a.name.localeCompare(b.name));
}

export function itemImagePresentation(image?: ImageReference, variation?: string) {
  const panels = itemImagePanels(image);
  const panel =
    panels.find((p) => p.name === variation) ?? panels.find((p) => p.name === image?.selectedPanel?.name) ?? panels[0];
  const isSheet = !!(image?.panels || image?.selectedPanel);
  // Never accidentally expose the entire sheet when all its panels are disabled/invalid.
  const url =
    isSheet && !panel
      ? undefined
      : isSheet
        ? image?.original || image?.large || image?.small
        : image?.large || image?.original || image?.small;
  return { url, panel, panels };
}
